import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Flags for refresh token handling
let isRefreshing = false;
let failedQueue = [];

// Resolve/Re-run queued requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// -----------------------------
// REQUEST INTERCEPTOR
// -----------------------------
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// -----------------------------
// RESPONSE INTERCEPTOR
// -----------------------------
API.interceptors.response.use(
  (res) => res,

  async (error) => {
    const originalRequest = error.config;

    // ---------- If Access Token Expired ----------
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If refresh request is already running → queue others
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}auth/refresh-token`, {
          refreshToken,
        });

        const newAccessToken = data.data.token;
        const newRefreshToken = data.data.refreshToken;

        // Save new tokens
        localStorage.setItem("token", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Update all future requests
        API.defaults.headers.Authorization = `Bearer ${newAccessToken}`;

        // Resolve queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        return API(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // For all other errors
    return Promise.reject(error);
  }
);

export default API;
